// src/pages/DocketConformation-page/DocketConformation.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaCheckCircle, FaPlus, FaMinus, FaFilePdf, FaSearch } from 'react-icons/fa';
import { generateDocketPDF } from '../../utils/generateDocketPDF'; // adjust path according to your folder structure

import BASE_URL from '../../api';

const API_BASE = `${BASE_URL}/api`;

const ITEMS_PER_PAGE = 10;

const DocketConfirmationPage = () => {
  const token = localStorage.getItem('token');
  const [dockets, setDockets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editDocket, setEditDocket] = useState(null);
  const [formData, setFormData] = useState({});
  const [measurements, setMeasurements] = useState([]);

  const [customerPrices, setCustomerPrices] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // ────── FETCH CUSTOMER PRICE SETTINGS ──────
  const fetchCustomerPrices = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/master/customerpricesetting`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.data && Array.isArray(json.data)) {
        setCustomerPrices(json.data);
      } else if (Array.isArray(json)) {
        setCustomerPrices(json);
      }
    } catch (err) {
      console.error('Error fetching customer price settings:', err);
    }
  };

  // Helper: Get Volume Factor from Customer Rate Master or fallback to 4500
  const getVolumeFactor = (docket) => {
    if (!docket) return 4500;
    const customerId = docket.consigneeid?._id || docket.consigneeid || docket.clientid?._id || docket.clientid || docket.consignorid?._id || docket.consignorid;
    if (customerId && customerPrices.length > 0) {
      const match = customerPrices.find(p => {
        const pCustId = p.customerId?._id || p.customerId;
        return String(pCustId) === String(customerId);
      });
      if (match && Number(match.volume) > 0) {
        return Number(match.volume);
      }
    }
    if (Number(docket.volumeFactor) > 0) {
      return Number(docket.volumeFactor);
    }
    return 4500;
  };

  // Helper: Get Origin Location from Customer Rate Master or Docket
  const getOriginLocation = (docket) => {
    if (!docket) return 'N/A';
    const customerId = docket.consigneeid?._id || docket.consigneeid || docket.clientid?._id || docket.clientid || docket.consignorid?._id || docket.consignorid;
    if (customerId && customerPrices.length > 0) {
      const match = customerPrices.find(p => {
        const pCustId = p.customerId?._id || p.customerId;
        return String(pCustId) === String(customerId);
      });
      if (match && match.fromLocationId) {
        if (Array.isArray(match.fromLocationId) && match.fromLocationId.length > 0) {
          const names = match.fromLocationId.map(l => l?.areaname || l?.hub || l?.name || (typeof l === 'string' && !/^[0-9a-fA-F]{24}$/.test(l) ? l : '')).filter(Boolean);
          if (names.length > 0) return names.join(', ');
        } else if (typeof match.fromLocationId === 'object') {
          const name = match.fromLocationId.areaname || match.fromLocationId.hub || match.fromLocationId.name;
          if (name) return name;
        }
      }
    }
    return docket.customerbranch?.branchname || docket.originlocation?.areaname || docket.originlocation?.hub || docket.origin || 'N/A';
  };

  // Helper: Get Destination Location from Customer Rate Master or Docket
  const getDestLocation = (docket) => {
    if (!docket) return 'N/A';
    const customerId = docket.consigneeid?._id || docket.consigneeid || docket.clientid?._id || docket.clientid || docket.consignorid?._id || docket.consignorid;
    if (customerId && customerPrices.length > 0) {
      const match = customerPrices.find(p => {
        const pCustId = p.customerId?._id || p.customerId;
        return String(pCustId) === String(customerId);
      });
      if (match && match.toLocationId) {
        if (Array.isArray(match.toLocationId) && match.toLocationId.length > 0) {
          const names = match.toLocationId.map(l => l?.areaname || l?.hub || l?.name || (typeof l === 'string' && !/^[0-9a-fA-F]{24}$/.test(l) ? l : '')).filter(Boolean);
          if (names.length > 0) return names.join(', ');
        } else if (typeof match.toLocationId === 'object') {
          const name = match.toLocationId.areaname || match.toLocationId.hub || match.toLocationId.name;
          if (name) return name;
        }
      }
    }
    return docket.destlocation?.areaname || docket.destlocation?.hub || docket.destination || 'N/A';
  };

  // ────── FETCH DOCKETS ──────
  const fetchDockets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/getctrl/getalldocketconfirmation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setDockets(json.data);
        setCurrentPage(1); // reset to first page after new data load
      } else {
        toast.error(json.message || 'Failed to load dockets');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error while fetching dockets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDockets();
      fetchCustomerPrices();
    }
  }, [token]);

  const isSuperAdmin = () => {
    const role = (localStorage.getItem('role') || localStorage.getItem('userRole') || '').toLowerCase();
    return role.includes('super admin') || role.includes('superadmin') || role.includes('super_admin') || role === 'admin';
  };

  // ────── DELETE DOCKET ──────
  const handleDelete = async (id) => {
    if (!isSuperAdmin()) {
      toast.error('Only Super Admin can delete dockets');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this docket confirmation?')) return;

    try {
      const res = await fetch(`${API_BASE}/onlinebookings/docketconfirmation/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (res.ok || json.success) {
        setDockets(prev => prev.filter(d => d._id !== id));
        toast.success('Docket deleted successfully');
      } else {
        toast.error(json.message || 'Failed to delete');
      }
    } catch (err) {
      console.error(err);
      toast.error('Delete operation failed');
    }
  };

  // ────── OPEN EDIT FORM ──────
  const handleEdit = (docket) => {
    setEditDocket(docket);
    setFormData({
      docket: docket.docket || '',
      referenceno: docket.referenceno || '',
      bookingdate: docket.bookingdate ? new Date(docket.bookingdate).toISOString().split('T')[0] : '',
      pincode: docket.pincode || '',
      isdoxornon: docket.isdoxornon || 0,
      mode: docket.mode || 0,
      noofpiece: docket.noofpiece || 0,
      length: docket.length || 0,
      breadth: docket.breadth || 0,
      height: docket.height || 0,
      actweight: docket.actweight || 0,
      cvalue: docket.cvalue || 0,
      remarks: docket.remarks || '',
      volweight: docket.volweight || 0,
      codapplicable: docket.codapplicable || 0,
      adjweight: docket.adjweight || 0,
      totvolweight: docket.totvolweight || 0,
      billingweight: docket.billingweight || 0,
      finalweight: docket.finalweight || 0,
      priceperkg: docket.priceperkg || 0,
      doccharge: docket.doccharge || 0,
      odacharge: docket.odacharge || 0,
      codcharge: docket.codcharge || 0,
      unloadingcharge: docket.unloadingcharge || 0,
      loadingcharge: docket.loadingcharge || 0,
      othercharge: docket.othercharge || 0,
      flatcharge: docket.flatcharge || 0,
      fovcharge: docket.fovcharge || 0,
      frieghtcharge: docket.frieghtcharge || 0,
      fuelcharge: docket.fuelcharge || 0,
      cgst: docket.cgst || 0,
      sgst: docket.sgst || 0,
      grandtotal: docket.grandtotal || 0,
      docketstatus: docket.docketstatus || 'Pending'
    });

    const volFactor = getVolumeFactor(docket);

    // Initialize measurements with unique id & recalculated volume
    setMeasurements(
      docket.docketmeasurement?.map(m => {
        const l = Number(m.length) || 0;
        const b = Number(m.breadth) || 0;
        const h = Number(m.height) || 0;
        const cubic = l * b * h;
        const calcVol = cubic > 0 ? cubic / volFactor : (Number(m.volume) || 0);
        const calcCft = cubic > 0 ? cubic / 1728 : (Number(m.cft) || 0);
        return {
          ...m,
          volume: calcVol,
          cft: calcCft,
          id: m._id || Math.random().toString(36).substr(2, 9)
        };
      }) || []
    );

    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditDocket(null);
    setFormData({});
    setMeasurements([]);
  };

  // ────── MEASUREMENT HANDLERS ──────
  const addMeasurement = () => {
    setMeasurements(prev => [
      ...prev,
      {
        id: Math.random().toString(36).substr(2, 9),
        docketnumber: formData.docket,
        length: 0,
        breadth: 0,
        height: 0,
        volume: 0,
        cft: 0,
        wt: 0,
        nos: 0,
        twt: 0
      }
    ]);
  };

  const removeMeasurement = (id) => {
    setMeasurements(prev => prev.filter(m => m.id !== id));
  };

  const updateMeasurement = (id, field, value) => {
    const volFactor = getVolumeFactor(editDocket);
    setMeasurements(prev =>
      prev.map(m => {
        if (m.id === id) {
          const updated = { ...m, [field]: Number(value) || 0 };
          const cubic = updated.length * updated.breadth * updated.height;
          // Auto-calculate volume = (L * B * H) / volumeFactor, cft = (L * B * H) / 1728, total weight
          updated.volume = cubic > 0 ? cubic / volFactor : 0;
          updated.cft = cubic > 0 ? cubic / 1728 : 0;
          updated.twt = updated.wt * updated.nos;
          return updated;
        }
        return m;
      })
    );
  };

  // ────── SUBMIT UPDATED DOCKET ──────
  const handleSubmit = async () => {
    if (!editDocket?._id) return;

    const payload = {
      ...formData,
      docketmeasurement: measurements.map(m => {
        const { id, ...rest } = m;
        return rest;
      })
    };

    try {
      const res = await fetch(`${API_BASE}/onlinebookings/docketconfirmation/${editDocket._id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (json.success) {
        toast.success('Docket updated successfully');
        await fetchDockets();
        handleCloseForm();
      } else {
        toast.error(json.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error during update');
    }
  };

  // ────── FILTERING & PAGINATION LOGIC ──────
  const filtered = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    if (!s) return dockets;
    return dockets.filter(d =>
      (d.docket || '').toLowerCase().includes(s) ||
      (d.referenceno || '').toLowerCase().includes(s) ||
      (d.consigneeid?.name || '').toLowerCase().includes(s)
    );
  }, [dockets, searchTerm]);

  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // ────── RENDER ──────
  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header Section */}
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Docket Confirmation Master</h2>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Toolbar */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <div className="relative w-full md:w-80">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search Docket No, Consignee..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
            </div>

            <button
              onClick={fetchDockets}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium shadow-sm"
            >
              Refresh Data
            </button>
          </div>

          {/* Table UI - Matches Pincode/Manifest Style */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">S.No</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Docket Info</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Consignee Details</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Origin → Dest</th>
                  <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                  <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-500 font-medium">Loading dockets...</td></tr>
                ) : paginated.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400 font-medium">No docket confirmations found</td></tr>
                ) : (
                  paginated.map((d, i) => {
                    // S.No Logic: Search Persistence Fix
                    const sNo = searchTerm
                      ? dockets.findIndex(orig => orig._id === d._id) + 1
                      : (currentPage - 1) * ITEMS_PER_PAGE + i + 1;

                    return (
                      <tr key={d._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-bold text-blue-700">{d.docket || 'N/A'}</span>
                            <span className="text-[10px] text-gray-400 uppercase">Ref: {d.referenceno || '-'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800">{d.consigneeid?.name || 'N/A'}</span>
                            <span className="text-xs text-gray-500">{d.consigneeid?.email || ''}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800 uppercase text-[11px]">
                              {getOriginLocation(d)} → {getDestLocation(d)}
                            </span>
                            <span className="text-[10px] text-blue-600 font-medium italic">
                              {d.mode === 1 ? 'SURFACE' : d.mode === 2 ? 'AIR' : d.mode === 3 ? 'TRAIN' : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${d.docketstatus === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                            {d.docketstatus?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => handleEdit(d)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition" title="Edit">
                              <FaEdit size={16} />
                            </button>
                            {isSuperAdmin() && (
                              <button onClick={() => handleDelete(d._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition" title="Delete">
                                <FaTrash size={16} />
                              </button>
                            )}
                            <button onClick={() => generateDocketPDF(d, getVolumeFactor(d), customerPrices)} className="p-1.5 text-green-600 hover:bg-green-50 rounded transition" title="Consignment Note">
                              <FaFilePdf size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <span className="text-sm text-gray-600 font-medium">
                Showing <span className="text-blue-600">{Math.min(filtered.length, (currentPage - 1) * ITEMS_PER_PAGE + 1)}</span> to <span className="text-blue-600">{Math.min(filtered.length, currentPage * ITEMS_PER_PAGE)}</span> of {filtered.length} entries
              </span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                  Previous
                </button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ────── EDIT MODAL ────── */}
        {showForm && editDocket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
              <h2 className="text-xl font-bold mb-6">Edit Docket: {editDocket.docket}</h2>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-medium mb-1">Docket No</label>
                  <input
                    type="text"
                    value={formData.docket}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-100 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Reference No</label>
                  <input
                    type="text"
                    value={formData.referenceno}
                    onChange={e => setFormData(p => ({ ...p, referenceno: e.target.value }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Booking Date</label>
                  <input
                    type="date"
                    value={formData.bookingdate}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-100 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Pincode</label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={e => setFormData(p => ({ ...p, pincode: e.target.value }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">DOX / NON-DOX</label>
                  <select
                    value={formData.isdoxornon}
                    onChange={e => setFormData(p => ({ ...p, isdoxornon: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  >
                    <option value={0}>NON-DOX</option>
                    <option value={1}>DOX</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Mode</label>
                  <select
                    value={formData.mode}
                    onChange={e => setFormData(p => ({ ...p, mode: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  >
                    <option value={0}>Air</option>
                    <option value={1}>Surface</option>
                    <option value={2}>Train</option>
                  </select>
                </div>
              </div>

              {/* Dimensions & Weight */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-medium mb-1">No of Pieces</label>
                  <input
                    type="number"
                    value={formData.noofpiece}
                    onChange={e => setFormData(p => ({ ...p, noofpiece: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Length (cm)</label>
                  <input
                    type="number"
                    value={formData.length}
                    onChange={e => setFormData(p => ({ ...p, length: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Breadth (cm)</label>
                  <input
                    type="number"
                    value={formData.breadth}
                    onChange={e => setFormData(p => ({ ...p, breadth: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Height (cm)</label>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={e => setFormData(p => ({ ...p, height: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Actual Weight</label>
                  <input
                    type="number"
                    value={formData.actweight}
                    onChange={e => setFormData(p => ({ ...p, actweight: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">C Value</label>
                  <input
                    type="number"
                    value={formData.cvalue}
                    onChange={e => setFormData(p => ({ ...p, cvalue: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
              </div>

              {/* Charges Section */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-medium mb-1">Price / Kg</label>
                  <input
                    type="number"
                    value={formData.priceperkg}
                    onChange={e => setFormData(p => ({ ...p, priceperkg: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Doc Charge</label>
                  <input
                    type="number"
                    value={formData.doccharge}
                    onChange={e => setFormData(p => ({ ...p, doccharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">ODA Charge</label>
                  <input
                    type="number"
                    value={formData.odacharge}
                    onChange={e => setFormData(p => ({ ...p, odacharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">COD Charge</label>
                  <input
                    type="number"
                    value={formData.codcharge}
                    onChange={e => setFormData(p => ({ ...p, codcharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Unloading Charge</label>
                  <input
                    type="number"
                    value={formData.unloadingcharge}
                    onChange={e => setFormData(p => ({ ...p, unloadingcharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Loading Charge</label>
                  <input
                    type="number"
                    value={formData.loadingcharge}
                    onChange={e => setFormData(p => ({ ...p, loadingcharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Other Charge</label>
                  <input
                    type="number"
                    value={formData.othercharge}
                    onChange={e => setFormData(p => ({ ...p, othercharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Flat Charge</label>
                  <input
                    type="number"
                    value={formData.flatcharge}
                    onChange={e => setFormData(p => ({ ...p, flatcharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">FOV Charge</label>
                  <input
                    type="number"
                    value={formData.fovcharge}
                    onChange={e => setFormData(p => ({ ...p, fovcharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Freight Charge</label>
                  <input
                    type="number"
                    value={formData.frieghtcharge}
                    onChange={e => setFormData(p => ({ ...p, frieghtcharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Fuel Charge</label>
                  <input
                    type="number"
                    value={formData.fuelcharge}
                    onChange={e => setFormData(p => ({ ...p, fuelcharge: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">CGST</label>
                  <input
                    type="number"
                    value={formData.cgst}
                    onChange={e => setFormData(p => ({ ...p, cgst: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">SGST</label>
                  <input
                    type="number"
                    value={formData.sgst}
                    onChange={e => setFormData(p => ({ ...p, sgst: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Grand Total</label>
                  <input
                    type="number"
                    value={formData.grandtotal}
                    onChange={e => setFormData(p => ({ ...p, grandtotal: Number(e.target.value) }))}
                    className="w-full p-2 border rounded text-xs"
                  />
                </div>
              </div>

              {/* Status & Remarks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-medium mb-1">Status *</label>
                  <select
                    value={formData.docketstatus}
                    onChange={e => setFormData(p => ({ ...p, docketstatus: e.target.value }))}
                    className="w-full p-2 border rounded text-xs"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Remarks</label>
                  <textarea
                    value={formData.remarks}
                    onChange={e => setFormData(p => ({ ...p, remarks: e.target.value }))}
                    className="w-full p-2 border rounded text-xs h-20 resize-none"
                    placeholder="Add any remarks or notes..."
                  />
                </div>
              </div>

              {/* Measurements Table */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-base font-semibold">Measurements</h3>
                  <button
                    onClick={addMeasurement}
                    className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm font-medium"
                  >
                    <FaPlus size={14} /> Add Row
                  </button>
                </div>

                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2">Length</th>
                        <th className="p-2">Breadth</th>
                        <th className="p-2">Height</th>
                        <th className="p-2">Volume</th>
                        <th className="p-2">CFT</th>
                        <th className="p-2">Wt</th>
                        <th className="p-2">Nos</th>
                        <th className="p-2">Total Wt</th>
                        <th className="p-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.map(m => (
                        <tr key={m.id} className="border-t">
                          <td className="p-1">
                            <input
                              type="number"
                              value={m.length}
                              onChange={e => updateMeasurement(m.id, 'length', e.target.value)}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-1">
                            <input
                              type="number"
                              value={m.breadth}
                              onChange={e => updateMeasurement(m.id, 'breadth', e.target.value)}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-1">
                            <input
                              type="number"
                              value={m.height}
                              onChange={e => updateMeasurement(m.id, 'height', e.target.value)}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-1 text-right font-medium">{m.volume.toFixed(2)}</td>
                          <td className="p-1 text-right font-medium">{m.cft.toFixed(4)}</td>
                          <td className="p-1">
                            <input
                              type="number"
                              value={m.wt}
                              onChange={e => updateMeasurement(m.id, 'wt', e.target.value)}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-1">
                            <input
                              type="number"
                              value={m.nos}
                              onChange={e => updateMeasurement(m.id, 'nos', e.target.value)}
                              className="w-full p-1 border rounded text-xs"
                            />
                          </td>
                          <td className="p-1 text-right font-medium">{m.twt.toFixed(2)}</td>
                          <td className="p-1 text-center">
                            <button
                              onClick={() => removeMeasurement(m.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <FaMinus size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {measurements.length === 0 && (
                        <tr>
                          <td colSpan={9} className="text-center py-4 text-gray-500">
                            No measurements added yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {formData.docketstatus === 'Completed' && (
                <div className="bg-green-50 border border-green-200 rounded p-4 mb-6 flex items-center gap-3 text-green-800">
                  <FaCheckCircle size={20} />
                  <span>
                    This docket will be marked as <strong>Completed</strong> upon saving.
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-4">
                <button
                  onClick={handleCloseForm}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  Update & Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocketConfirmationPage;